import { Routes } from "@angular/router";
import { authGuard } from "./guards/auth.guard";
import { publicGuard } from "./guards/public.guard";
import { LayoutComponent } from "./layout/layout.component";
import { rolesGuard } from "./guards/roles.guard";
import { UserRole } from "./models/permissions.model";

export const routes: Routes = [
	{
		path: "login",
		canActivate: [publicGuard],
		loadComponent: () => import("./pages/login/login.component").then((m) => m.LoginComponent),
	},
	{
		path: "auth/email-link-signin",
		canActivate: [publicGuard],
		loadComponent: () =>
			import("./pages/auth/email-link-signin/email-link-signin.component").then(
				(m) => m.EmailLinkSigninComponent,
			),
	},
	{
		path: "auth/wrong-device",
		canActivate: [publicGuard],
		loadComponent: () =>
			import("./pages/auth/wrong-device/wrong-device.component").then(
				(m) => m.WrongDeviceComponent,
			),
	},
	{
		path: "",
		component: LayoutComponent,
		canActivate: [authGuard],
		children: [
			{
				path: "home",
				loadComponent: () =>
					import("./pages/dashboard/dashboard.component").then(
						(m) => m.DashboardComponent,
					),
			},
			{
				path: "",
				redirectTo: "home",
				pathMatch: "full",
			},
			{
				path: "dashboard",
				redirectTo: "global-management",
				pathMatch: "full",
			},
			{
				path: "profile",
				loadComponent: () =>
					import("./pages/profile/profile.component").then((m) => m.ProfileComponent),
			},
			{
				path: "global-management",
				loadComponent: () =>
					import("./pages/global-management/global-management.component").then(
						(m) => m.GlobalManagementComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/admins",
				loadComponent: () =>
					import(
						"./pages/global-management/admins/global-admins-list/global-admins-list.component"
					).then((m) => m.GlobalAdminsListComponent),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/admins/new",
				loadComponent: () =>
					import(
						"./pages/global-management/admins/global-admin-form/global-admin-form.component"
					).then((m) => m.GlobalAdminFormComponent),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/admins/:id/edit",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/users",
				loadComponent: () =>
					import("./pages/global-management/users/users-list/users-list.component").then(
						(m) => m.UsersListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/users/new",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/users/:id/edit",
				loadComponent: () =>
					import("./pages/global-management/users/user-form/user-form.component").then(
						(m) => m.UserFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "global-management/feedback",
				loadComponent: () =>
					import("./pages/feedback/list/feedback-list.component").then(
						(m) => m.FeedbackListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN],
					permissionContext: "global",
				},
			},
			{
				path: "partners/:id/imports",
				loadComponent: () =>
					import("./pages/imports/import.component").then((m) => m.ImportComponent),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
					permissionContext: "partner",
				},
			},
			{
				path: "partners",
				loadComponent: () =>
					import("./pages/partners/list/partners-list.component").then(
						(m) => m.PartnersListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
					permissionContext: "partners",
				},
			},
			{
				path: "partners/new",
				loadComponent: () =>
					import("./pages/partners/form/partner-form.component").then(
						(m) => m.PartnerFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN],
					permissionContext: "partners",
				},
			},
			{
				path: "partners/:id/edit",
				loadComponent: () =>
					import("./pages/partners/form/partner-form.component").then(
						(m) => m.PartnerFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN],
					permissionContext: "partners",
				},
			},
			{
				path: "partners/:id",
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
					permissionContext: "partner",
				},
				children: [
					{
						path: "",
						redirectTo: "dashboard",
						pathMatch: "full",
					},
					{
						path: "dashboard",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/dashboard/partner-dashboard.component"
							).then((m) => m.PartnerDashboardComponent),
					},
					{
						path: "details",
						loadComponent: () =>
							import("./pages/partners/details/partner-details.component").then(
								(m) => m.PartnerDetailsComponent,
							),
					},
					{
						path: "edit",
						loadComponent: () =>
							import("./pages/partners/form/partner-form.component").then(
								(m) => m.PartnerFormComponent,
							),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN],
							permissionContext: "partner",
						},
					},
					{
						path: "organizations",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/organizations/organizations-list.component"
							).then((m) => m.OrganizationsListComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
					{
						path: "organizations/new",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/organizations/organization-form.component"
							).then((m) => m.OrganizationFormComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
					{
						path: "organizations/:orgId/edit",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/organizations/organization-form.component"
							).then((m) => m.OrganizationFormComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
					{
						path: "users",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/users/partner-users-list.component"
							).then((m) => m.PartnerUsersListComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
					{
						path: "users/new",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/users/partner-user-form.component"
							).then((m) => m.PartnerUserFormComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
					{
						path: "users/:userId/edit",
						loadComponent: () =>
							import(
								"./pages/partners/partner-management/users/partner-user-form.component"
							).then((m) => m.PartnerUserFormComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [UserRole.ADMIN, UserRole.PARTNER_ADMIN, UserRole.COACH],
							permissionContext: "partner",
						},
					},
				],
			},
			{
				path: "organizations",
				loadComponent: () =>
					import("./pages/organizations/list/organizations-list.component").then(
						(m) => m.OrganizationsListComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [
						UserRole.ADMIN,
						UserRole.ORG_ADMIN,
						UserRole.COACH,
						UserRole.PARTNER_ADMIN,
					],
					permissionContext: "organizations",
				},
			},
			{
				path: "organizations/new",
				loadComponent: () =>
					import("./pages/organizations/form/organization-form.component").then(
						(m) => m.OrganizationFormComponent,
					),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
			},
			{
				path: "organizations/:id/imports",
				loadComponent: () =>
					import("./pages/imports/import.component").then((m) => m.ImportComponent),
				canActivate: [rolesGuard],
				data: {
					roles: [UserRole.ADMIN, UserRole.ORG_ADMIN],
					permissionContext: "organizations",
				},
			},
			{
				path: "organizations/:id",
				canActivate: [rolesGuard],
				data: {
					roles: [
						UserRole.ADMIN,
						UserRole.ORG_ADMIN,
						UserRole.PARTNER_ADMIN,
						UserRole.COACH,
					],
					permissionContext: "organizations",
				},
				children: [
					{
						path: "",
						redirectTo: "dashboard",
						pathMatch: "full",
					},
					{
						path: "dashboard",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/dashboard/dashboard.component"
							).then((m) => m.DashboardComponent),
					},
					{
						path: "details",
						children: [
							{
								path: "",
								loadComponent: () =>
									import(
										"./pages/organizations/organization-management/details/details.component"
									).then((m) => m.DetailsComponent),
							},
							{
								path: "edit",
								loadComponent: () =>
									import(
										"./pages/organizations/organization-management/edit/edit-organization.component"
									).then((m) => m.EditOrganizationComponent),
								canActivate: [rolesGuard],
								data: {
									roles: [
										UserRole.ADMIN,
										UserRole.ORG_ADMIN,
										UserRole.PARTNER_ADMIN,
									],
									permissionContext: "organization",
								},
							},
						],
					},
					{
						path: "admin",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/admin/admin.component"
							).then((m) => m.AdminComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [
								UserRole.ADMIN,
								UserRole.ORG_ADMIN,
								UserRole.PARTNER_ADMIN,
								UserRole.COACH,
							],
							permissionContext: "organization",
						},
					},
					{
						path: "assessees",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/assessees/assessees.component"
							).then((m) => m.AssesseesComponent),
						canActivate: [rolesGuard],
						data: {
							roles: [
								UserRole.ADMIN,
								UserRole.ORG_ADMIN,
								UserRole.PARTNER_ADMIN,
								UserRole.COACH,
							],
							permissionContext: "organization",
						},
					},
					{
						path: "cohorts",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/cohorts/cohorts.component"
							).then((m) => m.CohortsComponent),
					},
					{
						path: "cohorts/add",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/cohorts/add/add-cohort.component"
							).then((m) => m.AddCohortComponent),
					},
					{
						path: "cohorts/create",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/cohorts/create/create-cohort.component"
							).then((m) => m.CreateCohortComponent),
					},
					{
						path: "cohorts/edit/:cohortId",
						loadComponent: () =>
							import(
								"./pages/organizations/organization-management/cohorts/edit/edit-cohort.component"
							).then((m) => m.EditCohortComponent),
					},
				],
			},
			{
				path: "assessments",
				redirectTo: "questionnaires",
				pathMatch: "full",
			},
			{
				path: "questionnaires",
				loadComponent: () =>
					import("./pages/questionnaires/questionnaires.component").then(
						(m) => m.QuestionnairesComponent,
					),
				children: [
					{
						path: "",
						loadComponent: () =>
							import(
								"./pages/questionnaires/questionnaires-list/questionnaires-list.component"
							).then((m) => m.QuestionnairesListComponent),
					},
					{
						path: "new",
						loadComponent: () =>
							import(
								"./pages/questionnaires/questionnaire-form/questionnaire-form.component"
							).then((m) => m.QuestionnaireFormComponent),
					},
					{
						path: "edit/:id",
						loadComponent: () =>
							import(
								"./pages/questionnaires/questionnaire-form/questionnaire-form.component"
							).then((m) => m.QuestionnaireFormComponent),
					},
				],
			},
			{
				path: "icons",
				loadComponent: () =>
					import("./pages/icons/icons.component").then(
						(m) => m.IconsComponent,
					),
			},
		],
	},
];
